import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import Seo from "@/components/Seo";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Send, Lock, CheckCircle2, MapPin, Sparkles, ClipboardList, DollarSign, Clock, ClipboardCheck, FileText, ExternalLink, MessageCircle, Zap, UserCheck, Briefcase, Download } from "lucide-react";

function stripSummary(text) {
  return text.replace(/<SUMMARY>[\s\S]*?<\/SUMMARY>/g, "").trim();
}

function chatPlaceholder(canChat, hasPremium) {
  if (!canChat) return "Interview complete · unlock to continue";
  if (hasPremium) return "Ask for guidance…";
  return "Type your response…";
}

export default function ProjectPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [livechatActive, setLivechatActive] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    const [proj, me] = await Promise.all([
      api.get(`/projects/${id}`),
      api.get("/auth/me").catch(() => ({ data: {} })),
    ]);
    setProject(proj.data);
    setLivechatActive(!!me.data?.livechat_active);
    if (proj.data.messages.length === 0) {
      const { data: kd } = await api.post(`/projects/${id}/interview/start`);
      setProject((p) => ({ ...p, messages: kd.messages }));
    }
  }, [id]);

  useEffect(() => { load().catch(() => setError("Failed to load project")); }, [load]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [project?.messages]);

  const send = async () => {
    if (!input.trim() || sending) return;
    setSending(true); setError("");
    const optimistic = { role: "user", content: input, ts: new Date().toISOString() };
    setProject((p) => ({ ...p, messages: [...p.messages, optimistic] }));
    const msg = input; setInput("");
    try {
      const { data } = await api.post(`/projects/${id}/interview/message`, { message: msg });
      setProject((p) => ({ ...p, messages: data.messages, summary: data.summary || p.summary, status: data.summary ? "summary" : p.status }));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSending(false); }
  };

  const checkout = async (packageId) => {
    setCheckoutBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        project_id: id,
        package_id: packageId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setCheckoutBusy(false);
    }
  };

  const hasSummary = project ? !!project.summary : false;
  const hasPremium = project ? project.status === "paid" && project.premium_report : false;
  const canChat = project ? (!hasSummary || hasPremium || livechatActive) : false;

  if (!project) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "var(--bw-bg-primary)" }}>
        <Header />
        <div className="max-w-6xl mx-auto p-8"><div className="label-overline">Loading project…</div></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bw-bg-primary)" }}>
      <Seo
        title={`${project.project_type} Project · ${project.address} — BuildWise AI`}
        description={`AI-guided ${project.project_type.toLowerCase()} permit analysis for ${project.address}. Jurisdiction: ${project.jurisdiction || "San Diego County"}.`}
        path={`/project/${project.id}`}
      />
      <Header />
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-10">
        {/* Header block */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
          <div className="md:col-span-8">
            <div className="label-overline mb-2">{project.project_type} Project</div>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight" style={{ color: "var(--bw-text-primary)" }} data-testid="project-title">
              {project.address}
            </h1>
            <div className="flex items-center gap-2 mt-3 text-sm" style={{ color: "var(--bw-text-secondary)" }}>
              <MapPin className="w-4 h-4" strokeWidth={1.5} />
              {project.jurisdiction || "Jurisdiction unknown"}
            </div>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <button onClick={() => navigate("/dashboard")} className="btn-outline text-sm" data-testid="back-dashboard">
              ← Dashboard
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Chat column */}
          <div className="lg:col-span-7">
            <div className="bw-card flex flex-col" style={{ height: "70vh" }}>
              <div className="px-6 py-4 border-b flex items-center gap-2" style={{ borderColor: "var(--bw-border)" }}>
                <Sparkles className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--bw-brand)" }} />
                <div className="label-overline">AI Interview</div>
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-messages">
                {project.messages.map((m, i) => {
                  const content = m.role === "assistant" ? stripSummary(m.content) : m.content;
                  if (!content) return null;
                  const key = `${m.ts || 't'}-${m.role}-${i}`;
                  return (
                    <div key={key} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                      <div className={m.role === "user" ? "chat-bubble-user" : "chat-bubble-ai"} data-testid={`msg-${i}`}>
                        {content}
                      </div>
                    </div>
                  );
                })}
                {sending && <div className="chat-bubble-ai animate-pulse w-24">…</div>}
                <div ref={bottomRef} />
              </div>
              <div className="p-4 border-t flex gap-2" style={{ borderColor: "var(--bw-border)" }}>
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={chatPlaceholder(canChat, hasPremium)}
                  className="bw-input"
                  disabled={sending || !canChat}
                  data-testid="chat-input"
                />
                <button onClick={send} disabled={sending || !input.trim() || !canChat} className="btn-primary" data-testid="chat-send">
                  <Send className="w-4 h-4" strokeWidth={2} />
                </button>
              </div>
            </div>
            {error && <div className="mt-3 text-sm" style={{ color: "var(--bw-error)" }} data-testid="project-error">{error}</div>}
          </div>

          {/* Sidebar column */}
          <div className="lg:col-span-5 space-y-4">
            {hasSummary && !hasPremium && project.concierge_status !== "requested" && (
              <div className="bw-card p-6" data-testid="free-summary-card">
                <div className="label-overline mb-3">Free Summary</div>
                <SummaryView summary={project.summary} />
                <div className="border-t mt-6 pt-6" style={{ borderColor: "var(--bw-border)" }}>
                  <div className="label-overline mb-2" style={{ color: "var(--bw-accent)" }}>Unlock Full Report</div>
                  <p className="text-sm mb-4" style={{ color: "var(--bw-text-secondary)" }}>
                    Get the permit checklist, fees, timelines, inspections, and official links.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => checkout("essential")} disabled={checkoutBusy} className="btn-outline" data-testid="checkout-essential">
                      <div className="text-left">
                        <div className="text-xs uppercase tracking-widest">Essential</div>
                        <div className="font-display font-black text-xl">$49</div>
                      </div>
                    </button>
                    <button onClick={() => checkout("professional")} disabled={checkoutBusy} className="btn-accent flex-col items-start" data-testid="checkout-professional">
                      <div className="text-left w-full">
                        <div className="text-xs uppercase tracking-widest opacity-90">Professional</div>
                        <div className="font-display font-black text-xl">$99</div>
                      </div>
                    </button>
                  </div>
                  {checkoutBusy && <div className="mt-3 text-xs" style={{ color: "var(--bw-text-tertiary)" }}>Redirecting to Stripe…</div>}
                </div>

                <div className="border-t mt-6 pt-6" style={{ borderColor: "var(--bw-border)" }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Briefcase className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--bw-accent)" }} />
                    <div className="label-overline" style={{ color: "var(--bw-accent)" }}>Or let us handle it</div>
                  </div>
                  <p className="text-sm mb-4" style={{ color: "var(--bw-text-secondary)" }}>
                    Our human experts prepare, submit, and manage your permits end-to-end. Dedicated project manager included.
                  </p>
                  <button
                    onClick={() => checkout("concierge")}
                    disabled={checkoutBusy}
                    className="btn-outline w-full flex items-center justify-between"
                    style={{ borderColor: "var(--bw-accent)" }}
                    data-testid="checkout-concierge"
                  >
                    <span className="flex items-center gap-2 font-semibold text-left">
                      <UserCheck className="w-4 h-4" strokeWidth={2} />
                      <span>
                        <div>Concierge · Done-for-you</div>
                        <div className="text-[10px] font-normal uppercase tracking-widest" style={{ color: "var(--bw-text-tertiary)" }}>Starting price</div>
                      </span>
                    </span>
                    <span className="font-display font-black text-lg">$299</span>
                  </button>
                </div>

                {!livechatActive && (
                  <div className="border-t mt-6 pt-6" style={{ borderColor: "var(--bw-border)" }}>
                    <div className="flex items-center gap-2 mb-2">
                      <MessageCircle className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--bw-brand)" }} />
                      <div className="label-overline">Or keep chatting</div>
                    </div>
                    <p className="text-sm mb-4" style={{ color: "var(--bw-text-secondary)" }}>
                      Have follow-up questions? Unlock 30 days of unlimited AI chat on all your projects.
                    </p>
                    <button
                      onClick={() => checkout("livechat_30d")}
                      disabled={checkoutBusy}
                      className="btn-outline w-full flex items-center justify-between"
                      data-testid="checkout-livechat"
                    >
                      <span className="flex items-center gap-2 font-semibold">
                        <Zap className="w-4 h-4" strokeWidth={2} /> Live AI Chat · 30 days
                      </span>
                      <span className="font-display font-black text-lg">$9.99</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {project.concierge_status === "requested" && (
              <div className="bw-card p-6" style={{ borderColor: "var(--bw-accent)", borderWidth: 2 }} data-testid="concierge-active-card">
                <div className="flex items-center gap-2 mb-3">
                  <Briefcase className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--bw-accent)" }} />
                  <div className="label-overline" style={{ color: "var(--bw-accent)" }}>Concierge Requested</div>
                </div>
                <h3 className="font-display font-bold text-2xl mb-3" style={{ color: "var(--bw-text-primary)" }}>
                  Your permit expert is on it.
                </h3>
                <p className="text-sm mb-5 leading-relaxed" style={{ color: "var(--bw-text-secondary)" }}>
                  A senior BuildWise permit specialist will review your project and reach out within <strong>24 hours</strong> with a firm quote and next steps. No further payment needed until you approve the scope.
                </p>
                <div className="space-y-2 text-sm" style={{ color: "var(--bw-text-primary)" }}>
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" style={{ color: "var(--bw-success)" }} /> Intake received</div>
                  <div className="flex items-center gap-2 opacity-50"><Clock className="w-4 h-4" /> Specialist review (in progress)</div>
                  <div className="flex items-center gap-2 opacity-40"><Clock className="w-4 h-4" /> Firm quote & scope call</div>
                  <div className="flex items-center gap-2 opacity-40"><Clock className="w-4 h-4" /> Documents prepared & submitted</div>
                  <div className="flex items-center gap-2 opacity-40"><Clock className="w-4 h-4" /> Permit issued</div>
                </div>
              </div>
            )}

            {livechatActive && !hasPremium && (
              <div className="bw-card p-5" style={{ borderColor: "var(--bw-brand)", borderWidth: 2 }} data-testid="livechat-active-card">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4" strokeWidth={2} style={{ color: "var(--bw-brand)" }} />
                  <div className="label-overline">Live Chat Active</div>
                </div>
                <p className="text-sm" style={{ color: "var(--bw-text-secondary)" }}>
                  Ask any follow-up question about your project — your BuildWise AI advisor is on-call.
                </p>
              </div>
            )}

            {!hasSummary && !hasPremium && (
              <div className="bw-card p-6" data-testid="interview-hint">
                <Lock className="w-8 h-8 mb-3" strokeWidth={1.5} style={{ color: "var(--bw-text-tertiary)" }} />
                <div className="font-display font-bold text-lg mb-2">Complete the interview</div>
                <p className="text-sm" style={{ color: "var(--bw-text-secondary)" }}>
                  Answer the AI's questions to unlock your free feasibility summary.
                </p>
              </div>
            )}

            {project.jurisdiction && (
              <Link
                to={`/documents/${encodeURIComponent(project.jurisdiction)}`}
                className="bw-card p-5 block hover:bg-tan transition-all"
                data-testid="link-jurisdiction-docs"
              >
                <div className="label-overline mb-2">Official Documents</div>
                <div className="flex items-center justify-between">
                  <div className="font-display font-bold text-base leading-tight" style={{ color: "var(--bw-brand)" }}>
                    {project.jurisdiction}
                  </div>
                  <ExternalLink className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} style={{ color: "var(--bw-brand)" }} />
                </div>
                <div className="text-xs mt-2" style={{ color: "var(--bw-text-secondary)" }}>
                  View portal, forms & handouts
                </div>
              </Link>
            )}

            {hasPremium && (
              <PremiumReportView report={project.premium_report} tier={project.premium_tier} projectId={project.id} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryView({ summary }) {
  const items = [
    { label: "Feasibility", value: summary.feasibility, icon: CheckCircle2 },
    { label: "Permit Complexity", value: summary.permit_complexity, icon: ClipboardList },
    { label: "Timeline", value: summary.estimated_timeline_weeks, icon: Clock },
    { label: "Cost Range", value: summary.estimated_cost_range, icon: DollarSign },
  ];
  return (
    <div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        {items.map((it) => (
          <div key={it.label} className="border p-3" style={{ borderColor: "var(--bw-border)" }} data-testid={`summary-${it.label.toLowerCase().replace(/\s+/g,'-')}`}>
            <div className="text-xs uppercase tracking-widest font-bold" style={{ color: "var(--bw-brand)" }}>{it.label}</div>
            <div className="font-display font-bold text-lg mt-1 capitalize" style={{ color: "var(--bw-text-primary)" }}>{it.value}</div>
          </div>
        ))}
      </div>
      {summary.key_considerations?.length > 0 && (
        <div className="mb-4">
          <div className="label-overline mb-2">Key Considerations</div>
          <ul className="text-sm space-y-1" style={{ color: "var(--bw-text-secondary)" }}>
            {summary.key_considerations.map((k, i) => (<li key={`kc-${i}-${k.slice(0,20)}`}>• {k}</li>))}
          </ul>
        </div>
      )}
      {summary.next_steps?.length > 0 && (
        <div>
          <div className="label-overline mb-2">Next Steps</div>
          <ul className="text-sm space-y-1" style={{ color: "var(--bw-text-secondary)" }}>
            {summary.next_steps.map((k, i) => (<li key={`ns-${i}-${k.slice(0,20)}`}>• {k}</li>))}
          </ul>
        </div>
      )}
    </div>
  );
}

function PremiumReportView({ report, tier, projectId }) {
  const downloadPdf = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/report.pdf`;
    // withCredentials cookie will be sent automatically for same-site request
    window.open(url, "_blank", "noopener,noreferrer");
  };
  return (
    <div className="space-y-4" data-testid="premium-report">
      <div className="bw-card p-6" style={{ borderColor: "var(--bw-success)", borderWidth: 2 }}>
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 className="w-5 h-5" style={{ color: "var(--bw-success)" }} />
          <div className="label-overline" style={{ color: "var(--bw-success)" }}>
            {tier === "professional" ? "Professional" : "Essential"} Report Unlocked
          </div>
        </div>
        <p className="text-sm leading-relaxed mt-2" style={{ color: "var(--bw-text-secondary)" }}>
          {report.executive_summary}
        </p>
        <button
          onClick={downloadPdf}
          className="btn-primary mt-5 w-full flex items-center justify-center gap-2"
          data-testid="download-report-pdf"
        >
          <Download className="w-4 h-4" strokeWidth={2} /> Download PDF Report
        </button>
      </div>

      <ReportSection icon={ClipboardCheck} title="Permit Checklist">
        <ul className="space-y-2">
          {report.permit_checklist?.map((p, i) => (
            <li key={`pc-${i}-${p.name || ''}`} className="flex gap-2 text-sm">
              <span>{p.required ? "☑" : "☐"}</span>
              <span><strong>{p.name}</strong> — <span style={{ color: "var(--bw-text-secondary)" }}>{p.notes}</span></span>
            </li>
          ))}
        </ul>
      </ReportSection>

      <ReportSection icon={DollarSign} title={`Estimated Fees · ${report.estimated_total_fees || ""}`}>
        <ul className="space-y-1 text-sm">
          {report.estimated_fees?.map((f, i) => (
            <li key={`fee-${i}-${f.item || ''}`} className="flex justify-between border-b py-1" style={{ borderColor: "var(--bw-border)" }}>
              <span>{f.item}</span>
              <span className="font-semibold">{f.amount_usd}</span>
            </li>
          ))}
        </ul>
      </ReportSection>

      <ReportSection icon={Clock} title="Timeline">
        <ul className="space-y-2 text-sm">
          {report.timeline?.map((t, i) => (
            <li key={`tl-${i}-${t.phase || ''}`}><strong>{t.phase}</strong> · {t.duration}<div style={{ color: "var(--bw-text-secondary)" }}>{t.notes}</div></li>
          ))}
        </ul>
      </ReportSection>

      <ReportSection icon={ClipboardList} title="Inspections">
        <ul className="space-y-1 text-sm">
          {report.inspections?.map((ins, i) => (
            <li key={`ins-${i}-${ins.name || ''}`}><strong>{ins.name}</strong> · {ins.when}<div style={{ color: "var(--bw-text-secondary)" }}>{ins.notes}</div></li>
          ))}
        </ul>
      </ReportSection>

      <ReportSection icon={FileText} title="Required Documents">
        <ul className="space-y-1 text-sm">
          {report.required_documents?.map((d, i) => (<li key={`rd-${i}-${d.slice(0,20)}`}>• {d}</li>))}
        </ul>
      </ReportSection>

      <ReportSection icon={ExternalLink} title="Official Links">
        <ul className="space-y-1 text-sm">
          {report.official_links?.map((l, i) => (
            <li key={`ol-${i}-${l.url || l.title || ''}`}><a href={l.url} target="_blank" rel="noopener noreferrer" className="underline font-semibold" style={{ color: "var(--bw-brand)" }}>{l.title}</a></li>
          ))}
        </ul>
      </ReportSection>

      <ReportSection icon={Sparkles} title="AI Guidance">
        <ul className="space-y-2 text-sm">
          {report.ai_guidance?.map((g, i) => (<li key={`ai-${i}-${g.slice(0,20)}`}>• {g}</li>))}
        </ul>
      </ReportSection>
    </div>
  );
}

function ReportSection({ icon: Icon, title, children }) {
  return (
    <div className="bw-card p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--bw-brand)" }} />
        <div className="font-display font-bold text-base" style={{ color: "var(--bw-text-primary)" }}>{title}</div>
      </div>
      {children}
    </div>
  );
}
